import { requestKey } from '@qcdoc/shared/redux/requests.ts';
import { vendorService } from '@qcdoc/shared/services/repositories/vendorService.ts';
import { extractErrorMessage } from '@qcdoc/shared/shared/utils/errorMessage.ts';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { VendorVqcaState } from '../slices/vendorVqcaSlice.ts';

const createFeatureThunk = createAsyncThunk.withTypes<{ state: { vendorVqca: VendorVqcaState }; rejectValue: string }>();

export const fetchVendorVqcas = createFeatureThunk('vendorVqca/fetchVendorVqcas', async (args: string | undefined, { rejectWithValue }) => {
  try {
    return (await vendorService.getVqcaList(args!));
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load VQCA data.'));
  }
}, { condition: (args) => !!args });

export const fetchVendorPendingVqcas = createFeatureThunk('vendorVqca/fetchVendorPendingVqcas', async (args: string | undefined, { rejectWithValue }) => {
  try {
    return (await vendorService.getVqcaPending(args!));
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load VQCA data.'));
  }
}, { condition: (args) => !!args });

export const fetchVendorActiveVqcas = createFeatureThunk('vendorVqca/fetchVendorActiveVqcas', async (args: string | undefined, { rejectWithValue }) => {
  try {
    return (await vendorService.getActiveVqcas(args!));
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load VQCA data.'));
  }
}, { condition: (args) => !!args });

export const fetchVendorVqcaSpecs = createFeatureThunk('vendorVqca/fetchVendorVqcaSpecs', async (args: { vendorId?: string; id?: string }, { rejectWithValue }) => {
  try {
    return (await vendorService.getVqcaSpecs(args.id!));
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load VQCA data.'));
  }
}, { condition: (args) => !!args.vendorId && !!args.id });

export const fetchVendorVqcaDocument = createFeatureThunk('vendorVqca/fetchVendorVqcaDocument', async (args: { vendorId?: string; id?: string }, { rejectWithValue }) => {
  try {
    return (await vendorService.getVqcaDocument(args.id!));
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to load VQCA data.'));
  }
}, { condition: (args) => !!args.vendorId && !!args.id });

export const mutateSignVendorVqca = createFeatureThunk('vendorVqca/mutateSignVendorVqca', async (args: { vendorId: string; id: string }, { rejectWithValue, getState, requestId, signal }) => {
  try {
    const result = await vendorService.signVqca(args.id);
    if (signal.aborted || getState().vendorVqca.mutations.signVendorVqca?.[requestKey(args.id)]?.requestId !== requestId) return rejectWithValue('Request cancelled.');
    return result;
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Unable to save VQCA changes.'));
  }
}, { condition: (args, { getState }) => !!args.vendorId && !!args.id && !Object.values(getState().vendorVqca.mutations).some(operations => operations[requestKey(args.id)]?.status === 'pending') });
